import {Grid, IconButton, Skeleton} from '@mui/material';
import api from 'api';
import {localDateFormat} from 'common/formats';
import {usePostApi} from 'common/hooks/usePostApi';
import AsyncContainer from 'components/AsyncContainer';
import DatePicker from 'components/inputs/DatePicker';
import Dropdown, {DividerOption, DropdownObjectOption} from 'components/inputs/Dropdown';
import dayjs, {Dayjs} from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import React, {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import RoiTable from './RoiTable';
import SummarizeIcon from '@mui/icons-material/Summarize';
import SettingsIcon from '@mui/icons-material/Settings';
import {Box} from '@mui/system';
import Loader from 'components/Loader';
import {useModal} from 'common/hooks/useModal';
import {RoiSettingsDialog} from './RoiSettingsDialog';
import {useLocalStorage} from 'common/hooks/useLocalStorage';
import {useIsPermittedByRole} from 'common/hooks/useIsPermittedByRole';
import {AdminResourceType} from 'types/diplomat/settings/adminroles/AdminResourceType';

dayjs.extend(quarterOfYear);

/**
 * Formats a date as "DD MMM" (e.g. "18 Dec"), or returns "Today" if the date is today.
 */
function formatDayMonthOrToday(date: Dayjs): string {
   return date.isSame(dayjs(), 'day') ? 'Today' : date.format('DD MMM');
}

/**
 * Formats a quarter range as "Qn (MMM 'YY – MMM 'YY)".
 */
function formatQuarterRange(start: Dayjs, end: Dayjs): string {
   const startStr = start.format("MMM 'YY");
   const endStr = end.format("MMM 'YY");
   return `Q${start.quarter()} (${startStr} – ${endStr})`;
}

/**
 * For month ranges we simply display the month and full year, e.g. "Aug 2023".
 */
function formatMonthRange(start: Dayjs): string {
   return start.format('MMM YYYY');
}

interface DateRange {
   start: Dayjs;
   end: Dayjs;
}
interface DateRangeItem {
   label: string;
   getRange: () => DateRange;
   formatSecondaryText: (start: Dayjs, end: Dayjs) => string;
}

/**
 * Predefined date ranges.
 * Adjust the logic/labels as needed to match your use case.
 */
const dateRanges: (DateRangeItem | DividerOption)[] = [
   {
      label: 'Today',
      getRange: () => ({
         start: dayjs().startOf('day'),
         end: dayjs().endOf('day'),
      }),
      formatSecondaryText: () => 'Today',
   },
   {
      label: 'Yesterday',
      getRange: () => ({
         start: dayjs().subtract(1, 'day').startOf('day'),
         end: dayjs().subtract(1, 'day').endOf('day'),
      }),
      formatSecondaryText: (start) => start.format('DD MMM'),
   },
   {
      label: 'Previous Week',
      getRange: () => ({
         start: dayjs().subtract(1, 'week').startOf('week'),
         end: dayjs().subtract(1, 'week').endOf('week'),
      }),
      formatSecondaryText: (start, end) => `${start.format('DD MMM')} – ${end.format('DD MMM')}`,
   },
   {divider: true},
   {
      label: 'Last 7 days',
      getRange: () => ({
         start: dayjs().subtract(7, 'day'),
         end: dayjs(),
      }),
      formatSecondaryText: (start, end) =>
         `${start.format('DD MMM')} – ${formatDayMonthOrToday(end)}`,
   },
   {
      label: 'Last 30 days',
      getRange: () => ({
         start: dayjs().subtract(30, 'day'),
         end: dayjs(),
      }),
      formatSecondaryText: (start, end) =>
         `${start.format('DD MMM')} – ${formatDayMonthOrToday(end)}`,
   },
   {
      label: 'Last 90 days',
      getRange: () => ({
         start: dayjs().subtract(90, 'day'),
         end: dayjs(),
      }),
      formatSecondaryText: (start, end) =>
         `${start.format('DD MMM')} – ${formatDayMonthOrToday(end)}`,
   },
   {divider: true},
   {
      label: 'Previous Month',
      getRange: () => ({
         start: dayjs().subtract(1, 'month').startOf('month'),
         end: dayjs().subtract(1, 'month').endOf('month'),
      }),
      formatSecondaryText: (start) => formatMonthRange(start),
   },
   {
      label: 'Previous Quarter',
      getRange: () => ({
         start: dayjs().subtract(1, 'quarter').startOf('quarter'),
         end: dayjs().subtract(1, 'quarter').endOf('quarter'),
      }),
      formatSecondaryText: (start, end) => formatQuarterRange(start, end),
   },
   {divider: true},
   {
      label: 'Week To Date',
      getRange: () => ({
         start: dayjs().startOf('week'),
         end: dayjs(),
      }),
      formatSecondaryText: (start, end) =>
         `${start.format('DD MMM')} – ${formatDayMonthOrToday(end)}`,
   },
   {
      label: 'Month To Date',
      getRange: () => ({
         start: dayjs().startOf('month'),
         end: dayjs(),
      }),
      formatSecondaryText: (start, end) =>
         `${start.format('DD MMM')} – ${formatDayMonthOrToday(end)}`,
   },
   {
      label: 'Quarter To Date',
      getRange: () => ({
         start: dayjs().startOf('quarter'),
         end: dayjs(),
      }),
      formatSecondaryText: (start, end) => formatQuarterRange(start, end),
   },
   {divider: true},
   {
      label: 'All Time',
      getRange: () => ({
         start: dayjs('1970-01-01'), // or whatever "all time" means for your app
         end: dayjs(),
      }),
      formatSecondaryText: (start, end) =>
         `${start.format('DD MMM YYYY')} – ${formatDayMonthOrToday(end)}`,
   },
   {
      label: 'Custom',
      getRange: () => ({
         // Typically you'd open a DateRangePicker here or handle it differently
         start: dayjs(),
         end: dayjs(),
      }),
      formatSecondaryText: () => '',
   },
];

const usePeriodCalculated = () => {
   const periods: ((DropdownObjectOption & DateRange) | DividerOption)[] = dateRanges.map(
      (item, i) => {
         if ((item as DividerOption).divider) return item as DividerOption;

         const d = item as DateRangeItem;
         const {start, end} = d.getRange();
         const labelInfo = d.formatSecondaryText(start, end);

         return {
            name: d.label,
            id: i,
            labelInfo,
            start,
            end,
            // divider: false,
         };
      }
   );
   const CUSTOM_ID = periods.length - 1;

   return {periods, CUSTOM_ID};
   //    const periods = [
   //       {id: i++, name: 'Today', period: {from: '', to: ''}},
   //       {id: i++, name: 'Yesterday', period: {from: '', to: ''}},
   //       {id: i++, name: 'Previous Week', period: {from: '', to: ''}},
   //       {id: i++, name: 'Last 7 days', period: {from: '', to: ''}},
   //       {id: i++, name: 'Last 30 days', period: {from: '', to: ''}},
   //       {id: i++, name: 'Last 90 days', period: {from: '', to: ''}},
   //       {id: i++, name: 'Previous Month', period: {from: '', to: ''}},
   //       {id: i++, name: 'Previous Quarter', period: {from: '', to: ''}},
   //       {id: i++, name: 'Week to Date', period: {from: '', to: ''}},
   //       {id: i++, name: 'Month to Date', period: {from: '', to: ''}},
   //       {id: i++, name: 'Quarter to Date', period: {from: '', to: ''}},
   //       {id: i++, name: 'All Time', period: {from: '', to: ''}},
   //       {id: i++, name: 'Custom', period: {from: '', to: ''}},
   //    ];
};

type Props = {
   // data: INav;
   onClose: () => void;
};
export const RoiReportForm = ({onClose}: Props) => {
   const {t} = useTranslation();
   const {periods: options, CUSTOM_ID} = usePeriodCalculated();
   const [periodId, setPeriodId] = useLocalStorage<number>('periodId', 0);
   const [customStart, setCustomStart] = React.useState<Dayjs>(dayjs().subtract(7, 'day'));
   const [customEnd, setCustomEnd] = React.useState<Dayjs>(dayjs());

   const isPermittedByRole = useIsPermittedByRole();

   const {request, state, loading} = usePostApi(api.roiReport);

   const {open, handleOpen, handleClose} = useModal();

   // useCallback

   useEffect(() => {
      const {start, end} = options[periodId] as DateRange;
      request(
         JSON.stringify({
            start_date: start.format(localDateFormat),
            end_date: end.format(localDateFormat),
         })
      );
   }, []);

   const loadCustom = useCallback(() => {
      request(
         JSON.stringify({
            start_date: customStart.format(localDateFormat),
            end_date: customEnd.format(localDateFormat),
         })
      );
   }, [request, customStart, customEnd]);

   return (
      <div>
         <Grid container spacing={3} alignItems="center">
            {isPermittedByRole(AdminResourceType.enum.SETTINGS) && (
               <Grid item xs={1}>
                  <IconButton onClick={handleOpen}>
                     <SettingsIcon />
                  </IconButton>
               </Grid>
            )}
            <Grid item xs={4}>
               <Dropdown
                  name="period"
                  label={t('Period')}
                  options={options}
                  onChange={(ev) => {
                     const id = Number(ev.target.value);
                     setPeriodId(id);
                     if (id !== CUSTOM_ID) {
                        const {start, end} = options[id] as DateRange;
                        request(
                           JSON.stringify({
                              start_date: start.format(localDateFormat),
                              end_date: end.format(localDateFormat),
                           })
                        );
                     }
                  }}
                  value={periodId}
               />
            </Grid>

            {periodId === CUSTOM_ID ? (
               <>
                  <Grid item xs={2}>
                     <DatePicker
                        label="Start Date"
                        value={customStart}
                        onChange={(newValue) => {
                           if (newValue) {
                              setCustomStart(newValue);
                           }
                        }}
                     />
                  </Grid>
                  <Grid item xs={2}>
                     <DatePicker
                        label="End Date"
                        value={customEnd}
                        onChange={(newValue) => {
                           if (newValue) {
                              setCustomEnd(newValue);
                           }
                        }}
                     />
                  </Grid>
                  <Grid item xs={1}>
                     {loading ? (
                        <Loader />
                     ) : (
                        <IconButton onClick={loadCustom}>
                           <SummarizeIcon />
                        </IconButton>
                     )}
                  </Grid>
               </>
            ) : (
               <Grid item>{loading && <Loader />}</Grid>
            )}
         </Grid>
         <Box sx={{height: 500}}>
            {state ? (
               <RoiTable data={state} />
            ) : (
               <Skeleton variant="rectangular" height="100%" width="100%" />
            )}
         </Box>

         {open && <RoiSettingsDialog onClose={handleClose} />}
      </div>
   );
};
